import React from 'react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import ProductPhotoSpec from '../components/ProductPhotoSpec';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import { HERO_MEDIA, PRODUCT_MEDIA } from '../config/media';
import { CRANE_MATS_HUB, CRANE_MAT_SPEC, CRANE_FAQS } from '../data/copy';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    productJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

const CRANE_HERO_FALLBACK = {
    hero: {
        eyebrow: CRANE_MATS_HUB.eyebrow,
        headline: CRANE_MATS_HUB.title,
        body: 'Designed to create stable, level surfaces for cranes and heavy equipment on soft, uneven, or environmentally sensitive ground.',
        image_url: HERO_MEDIA.cranePad.src,
    },
};

export default function CraneMats() {
    const content = usePageContent('products.crane-mats', CRANE_HERO_FALLBACK);
    const hero = content.hero || CRANE_HERO_FALLBACK.hero;
    return (
        <>
            <SEO
                title="Crane Mats - Outrigger Pads & Heavy-Lift Picks"
                description="Designed to create stable, level surfaces for cranes and heavy equipment on soft, uneven, or environmentally sensitive ground."
                cmsSlug="seo.crane-mats"
                path="/crane-mats"
                image={HERO_MEDIA.cranePad.src}
                jsonLd={[
                    orgJsonLd(),
                    productJsonLd({
                        name: 'Centurion Crane Mats',
                        description:
                            'Designed to create stable, level surfaces for cranes and heavy equipment on soft, uneven, or environmentally sensitive ground.',
                        slug: '/crane-mats',
                        material: 'Coastal Hem-Fir / Coastal Fir, #2 & BTR structural grade',
                        additionalProperty: [
                            { name: 'Width', minValue: 2, maxValue: 10, unitText: 'FT' },
                            { name: 'Length', minValue: 4, maxValue: 40, unitText: 'FT' },
                            { name: 'Thickness', minValue: 6, maxValue: 18, unitText: 'IN' },
                            { name: 'Bolt Pattern', value: 'Recessed, spot-welded nuts' },
                            { name: 'Build', value: 'Premium coastal-fir laminated timber, custom-built to spec' },
                        ],
                    }),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Crane Mats', path: '/crane-mats' },
                    ]),
                    faqJsonLd(CRANE_FAQS),
                ]}
            />

            <HeroPlaceholder
                mediaKey="cranePad"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <ProductPhotoSpec
                testId="crane-mat-photo-spec"
                photo={{
                    src: PRODUCT_MEDIA.craneMat.src,
                    alt: PRODUCT_MEDIA.craneMat.alt,
                    caption: '4 \u00d7 20 crane mat.',
                }}
                specTitle="Centurion crane mat - typical build."
                specBody="Final spec is set by your rigging plan and geotech. Centurion supports your engineering team with raw material specs and assembly drawings."
                specRows={CRANE_MAT_SPEC}
                specTableTitle="Crane Mat - Typical"
                captionOffsetX={-6}
            />

            <section
                className="border-y border-brand-hairline bg-brand-surface"
                data-testid="crane-mat-definition"
            >
                <div className="mx-auto max-w-3xl px-6 py-20 md:py-24">
                    <span className="eyebrow">What is a Crane Mat?</span>
                    <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                        Heavy-duty mats for level work surfaces.
                    </h2>
                    <p className="mt-6 font-sans text-base leading-relaxed text-brand-muted">
                        Crane mats are high-strength, heavy-duty mats, used to create stable, level work surfaces for cranes and heavy machinery on soft, uneven, or environmentally sensitive terrain. Options include notched crane mats for easy handling and transport and pipeline cable mats.
                    </p>
                    <p className="mt-5 font-sans text-base leading-relaxed text-brand-muted">
                        Crane mats are also called crane pads, outrigger pads, or heavy-lift pads in different regions of Canada and the US.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-5xl px-6 py-20 md:py-28" data-testid="crane-mat-options">
                <span className="eyebrow">Crane Mat Options</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Two purpose-built crane mat configurations.
                </h2>
                <p className="mt-5 max-w-2xl font-sans text-base text-brand-muted">
                    Centurion crane mats are custom-built to your rigging plan. Two
                    common configurations make up the bulk of what ships from our
                    Chemainus mill:
                </p>

                <div className="mt-12 grid gap-8 md:grid-cols-2 md:gap-10">
                    {/* 4 x 20 Pipeline Cable Mat */}
                    <article
                        className="border border-brand-hairline bg-brand-surface p-8"
                        data-testid="crane-option-pipeline-cable"
                    >
                        <h3 className="h-display text-xl sm:text-2xl">
                            {'4 \u00d7 20 Pipeline Cable Mat'}
                        </h3>
                        <ul className="mt-6 space-y-3 font-sans text-sm leading-relaxed text-brand-text">
                            <li className="flex items-start gap-3">
                                <span
                                    aria-hidden="true"
                                    className="mt-2 h-1.5 w-1.5 flex-none bg-brand-gold"
                                />
                                <span>
                                    {'5/8\u2033 \u00d7 6 \u00d7 26 IWRC Powerstrand wire rope'}
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span
                                    aria-hidden="true"
                                    className="mt-2 h-1.5 w-1.5 flex-none bg-brand-gold"
                                />
                                <span>
                                    HD Thimble, Flemish hydraulically pressed eyes on
                                    both ends, with formed shoes
                                </span>
                            </li>
                        </ul>
                    </article>

                    {/* 4 x 20 Notched Crane Mat */}
                    <article
                        className="border border-brand-hairline bg-brand-surface p-8"
                        data-testid="crane-option-notched"
                    >
                        <h3 className="h-display text-xl sm:text-2xl">
                            {'4 \u00d7 20 Notched Crane Mat'}
                        </h3>
                        <ul className="mt-6 space-y-3 font-sans text-sm leading-relaxed text-brand-text">
                            <li className="flex items-start gap-3">
                                <span
                                    aria-hidden="true"
                                    className="mt-2 h-1.5 w-1.5 flex-none bg-brand-gold"
                                />
                                <span>
                                    {'Oversized 1 1/4\u2033 steel bolts'}
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span
                                    aria-hidden="true"
                                    className="mt-2 h-1.5 w-1.5 flex-none bg-brand-gold"
                                />
                                <span>
                                    {'5\u2032 \u00d7 17\u2033 precision-cut notch at each end'}
                                </span>
                            </li>
                        </ul>
                        <p className="mt-6 font-sans text-sm leading-relaxed text-brand-muted">
                            Notched crane mats are heavy-duty timber mats designed
                            primarily for lifting, moving, and positioning using
                            cranes and excavators, with the notches providing
                            engineered pick points for chains or cables.
                        </p>
                    </article>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Heavy timber for heavy lifts.
                </h2>
                <div className="mt-8 space-y-6 font-sans text-base leading-relaxed text-brand-muted">
                    {CRANE_MATS_HUB.intro.map((p) => (
                        <p key={p.slice(0, 60)}>{p}</p>
                    ))}
                </div>
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                    <AeoCallout
                        testId="aeo-crane-coastal-fir"
                        q="Why coastal fir for crane mats?"
                        a="Coastal Hem-Fir / Coastal Fir #2 & BTR delivers the density and tight grain needed for repeat heavy-pick loads. Recessed, spot-welded bolt nuts resist vibration loosening over the lift duration. Build configurations vary by rigging plan and geotech."
                    />
                    <AeoCallout
                        testId="aeo-crane-typical-size"
                        q="What size are crane outrigger pads?"
                        a="Centurion crane mats are typically 4 ft × 8 ft, 4 ft × 12 ft or 4 ft × 16 ft, 12 in to 14 in thick laminated timber. Final size and load rating are signed off against your lift plan and the ground bearing capacity at the pad location."
                    />
                </div>
                <div className="mt-4">
                    <AeoCallout
                        testId="aeo-crane-load-capacity"
                        q="Typical Load Capacity"
                        a="Crane mat load capacity depends on mat thickness, span, and ground bearing conditions. Typical industry ranges run from heavy lift duty to outrigger pad applications - engineered values for your specific lift are provided on request."
                    />
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                <div className="grid gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16">
                    <div>
                        <span className="eyebrow">Common Applications</span>
                        <h3 className="h-display mt-6 text-2xl sm:text-3xl">
                            Where Centurion crane mats earn the lift.
                        </h3>
                    </div>
                    <ul className="grid gap-3 sm:grid-cols-2">
                        {CRANE_MATS_HUB.applications.map((a) => (
                            <li
                                key={a}
                                className="flex items-start gap-3 border border-brand-hairline bg-brand-surface p-4 font-sans text-sm text-brand-text"
                            >
                                <span
                                    aria-hidden="true"
                                    className="mt-1.5 h-1.5 w-1.5 flex-none bg-brand-gold"
                                />
                                {a}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about crane mats.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={CRANE_FAQS} />
                </div>
            </section>

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Got a lift plan? We’ll quote the mat package."
                body="Send the rigging spec and we’ll match the build - premium coastal-fir crane mats, custom-sized to your lift plan."
                cta={{ to: '/quote', label: 'Request a Spec Sheet' }}
            />
        </>
    );
}
